import React, { useState } from "react";
import { createPortal } from "react-dom";
import { supabase } from "../../../lib/supabase";
import { useRBAC } from "../../../hooks/useRBAC";
import { buildGhlContactUrl } from "./ghlContactUrl";

interface CardActionsProps {
  conversationId: string;
  /** ghl_contact_id for this conversation (from the grid's contactIdMap). Null = no GHL link. */
  contactId: string | null;
  locationId: string | null;
  /** Setter whose grid is being viewed — drives own-vs-any RBAC, same as BulkDismiss. */
  viewedSetterId?: string | null;
  currentUserGhlId?: string | null;
  /** Reuses the parent's bulk-dismiss aftermath (filters the card out + invalidates cache). */
  onAfterDismiss: (ids: string[], dismissType: "dismissed" | "dead") => void;
}

type DismissType = "dismissed" | "dead";

/**
 * S64 (Jun 2026) — per-card action row for ProofGridView (Layout B: a labeled row
 * directly under the card header). Lets a setter Open-in-GHL / Dismiss / Kill ONE
 * conversation without entering Select mode. Single-id calls into the SAME paths the
 * bulk action bar uses:
 *   • Dismiss / Kill → bulk_dismiss_conversations([id], type)
 *   • Open in GHL → buildGhlContactUrl + window.open (shared helper with BulkOpenInGHL).
 *
 * RBAC: Dismiss/Kill gated by canDismissOwn/Any (UX gate; the server-side RPC role gate is
 * the real enforcement). Open in GHL is ungated — it's a read-only deep link.
 */
export const CardActions: React.FC<CardActionsProps> = ({
  conversationId,
  contactId,
  locationId,
  viewedSetterId,
  currentUserGhlId,
  onAfterDismiss,
}) => {
  const { canDismissOwnConversations, canDismissAnyConversation } = useRBAC();

  const viewingOwnGrid =
    viewedSetterId != null && currentUserGhlId != null && viewedSetterId === currentUserGhlId;
  const canAct = viewingOwnGrid ? canDismissOwnConversations : canDismissAnyConversation;

  const [busy, setBusy] = useState<DismissType | null>(null);
  const [showKillConfirm, setShowKillConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockedUrl, setBlockedUrl] = useState<string | null>(null);

  const ghlDisabled = !locationId || !contactId;

  const handleOpenGhl = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!locationId || !contactId) return;
    const url = buildGhlContactUrl(locationId, contactId);
    // No "noopener" in features — per spec it returns null on success, which would fake a
    // popup-block. Sever opener manually right after open for the same security guarantee.
    const w = window.open(url, "_blank");
    if (w) {
      try {
        w.opener = null;
      } catch {
        /* cross-origin race: opener already inaccessible, which is the goal */
      }
    }
    if (!w || w.closed) setBlockedUrl(url);
    else setBlockedUrl(null);
  };

  const runDismiss = async (dismissType: DismissType) => {
    setBusy(dismissType);
    setError(null);
    try {
      if (!supabase) {
        setError("Database client not initialized");
        return;
      }
      const { error: rpcError } = await supabase.rpc("bulk_dismiss_conversations", {
        p_conversation_ids: [conversationId],
        p_dismiss_type: dismissType,
      });
      if (rpcError) {
        setError(rpcError.message);
        return;
      }
      onAfterDismiss([conversationId], dismissType);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 border-b border-black/[0.06] bg-black/[0.02]"
      data-testid="proof-card-actions"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={handleOpenGhl}
        disabled={ghlDisabled}
        data-testid="card-open-in-ghl"
        title={
          ghlDisabled
            ? "No GHL contact / location for this conversation"
            : "Open this contact in GHL"
        }
        className="liquid-glass-light px-2 py-0.5 token-10 text-glass-secondary hover:text-glass disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        ↗ GHL
      </button>
      {canAct && (
        <>
          <button
            type="button"
            onClick={() => runDismiss("dismissed")}
            disabled={busy !== null}
            data-testid="card-dismiss"
            title="Dismiss — reappears if the lead messages again"
            className="liquid-glass-light px-2 py-0.5 token-10 text-ds-amber hover:text-glass disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy === "dismissed" ? "Dismissing…" : "Dismiss"}
          </button>
          <button
            type="button"
            onClick={() => setShowKillConfirm(true)}
            disabled={busy !== null}
            data-testid="card-kill"
            title="Kill — never reappears, even if the lead messages again"
            className="liquid-glass-light px-2 py-0.5 token-10 text-ds-red hover:text-glass disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {busy === "dead" ? "Killing…" : "Kill"}
          </button>
        </>
      )}
      {error && (
        <span className="token-10 text-ds-red" data-testid="card-action-error" role="alert">
          {error}
        </span>
      )}
      {blockedUrl && (
        <a
          href={blockedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="token-10 text-ds-accent hover:underline"
          data-testid="card-ghl-fallback-link"
          onClick={(e) => e.stopPropagation()}
        >
          open ↗ (popup blocked)
        </a>
      )}
      {showKillConfirm &&
        typeof document !== "undefined" &&
        createPortal(
          // z-[9999] sits one tier above SetterProofModal (z-[9998]); detail-overlay, not
          // liquid-glass, per /css ::before z-index trap on overlays. Mirrors BulkDismiss's kill confirm.
          <div
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40"
            data-testid="card-kill-confirm-modal"
            onClick={(e) => {
              e.stopPropagation();
              setShowKillConfirm(false);
            }}
          >
            <div className="detail-overlay max-w-md p-6 m-4" onClick={(e) => e.stopPropagation()}>
              <h3 className="token-16 font-semibold text-glass mb-2">
                Permanently remove this conversation?
              </h3>
              <p className="token-13 text-glass-secondary mb-5">
                Killed conversations won&apos;t reappear in DM Analytics even if the lead messages
                again. Use Dismiss if you want it back when the lead replies.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowKillConfirm(false);
                  }}
                  data-testid="card-kill-cancel"
                  className="liquid-glass-light px-3 py-1 token-11 text-glass-secondary hover:text-glass"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowKillConfirm(false);
                    void runDismiss("dead");
                  }}
                  data-testid="card-kill-confirm"
                  className="liquid-glass-light px-3 py-1 token-11 text-ds-red hover:text-glass"
                >
                  Kill
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
